import type {
  MaintenanceTemplateVersionStatus,
  MaintenanceWorkOrderStatus,
} from '@gb-ticket/shared-types';

/** Work-order enum or the active flag on customers, projects, and installations. */
export type MaintenanceStatusInput = MaintenanceWorkOrderStatus | boolean;

const WORK_ORDER_LABELS: Record<MaintenanceWorkOrderStatus, string> = {
  planned: 'Geplant',
  assigned: 'Zugewiesen',
  in_progress: 'In Bearbeitung',
  finalizing: 'Abschluss läuft',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

const STATUS_INFO = 'border-status-info-border bg-status-info text-status-info-foreground';
const STATUS_SUCCESS =
  'border-status-success-border bg-status-success text-status-success-foreground';
const STATUS_WARNING =
  'border-status-warning-border bg-status-warning text-status-warning-foreground';
const STATUS_DANGER = 'border-status-danger-border bg-status-danger text-status-danger-foreground';
const STATUS_NEUTRAL =
  'border-status-neutral-border bg-status-neutral text-status-neutral-foreground';

/** Info ink on the card, so „Zugewiesen“ stays distinct from the filled „Geplant“ chip. */
const STATUS_INFO_QUIET = 'border-status-info-border bg-card text-status-info-foreground';
/** Warning ink on the card, so „Abschluss läuft“ stays distinct from „In Bearbeitung“. */
const STATUS_WARNING_QUIET = 'border-status-warning-border bg-card text-status-warning-foreground';

const INACTIVE_BADGE_CLASS = STATUS_NEUTRAL;

const WORK_ORDER_BADGE_CLASS: Record<MaintenanceWorkOrderStatus, string> = {
  planned: STATUS_INFO,
  assigned: STATUS_INFO_QUIET,
  in_progress: STATUS_WARNING,
  finalizing: STATUS_WARNING_QUIET,
  completed: STATUS_SUCCESS,
  cancelled: STATUS_DANGER,
};

const ACTIVE_BADGE_CLASS = 'border-border bg-card text-foreground';

const VERSION_LABELS: Record<MaintenanceTemplateVersionStatus, string> = {
  draft: 'Entwurf',
  published: 'Veröffentlicht',
  retired: 'Zurückgezogen',
};

export function maintenanceStatusLabel(status: MaintenanceStatusInput): string {
  if (typeof status === 'boolean') {
    return status ? 'Aktiv' : 'Inaktiv';
  }
  return WORK_ORDER_LABELS[status];
}

export const MAINTENANCE_WORK_ORDER_STATUS_OPTIONS: {
  value: MaintenanceWorkOrderStatus;
  label: string;
}[] = (
  [
    'planned',
    'assigned',
    'in_progress',
    'finalizing',
    'completed',
    'cancelled',
  ] as MaintenanceWorkOrderStatus[]
).map((value) => ({
  value,
  label: maintenanceStatusLabel(value),
}));

export const MAINTENANCE_ACTIVE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'true', label: maintenanceStatusLabel(true) },
  { value: 'false', label: maintenanceStatusLabel(false) },
];

export function maintenanceActiveSelectValue(isActive: boolean | undefined): string {
  if (isActive === true) {
    return 'true';
  }
  if (isActive === false) {
    return 'false';
  }
  return '';
}

export function maintenanceActiveFromSelect(value: string): boolean | undefined {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
}

export function maintenanceStatusBadgeClass(status: MaintenanceStatusInput): string {
  if (typeof status === 'boolean') {
    return status ? ACTIVE_BADGE_CLASS : INACTIVE_BADGE_CLASS;
  }
  return WORK_ORDER_BADGE_CLASS[status];
}

export function maintenanceTemplateVersionLabel(status: MaintenanceTemplateVersionStatus): string {
  return VERSION_LABELS[status];
}

export function maintenanceTemplateVersionBadgeClass(
  status: MaintenanceTemplateVersionStatus,
): string {
  if (status === 'published') {
    return WORK_ORDER_BADGE_CLASS.completed;
  }
  return INACTIVE_BADGE_CLASS;
}
